import { useState, useEffect } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useSubscriptionStatus } from './guards/SubscriptionGuard';
import AvisoEmpresa from './guards/AvisoEmpresa.jsx';
import { useAvisoEmpresa } from './guards/useAvisoEmpresa.js';
import AceptaLegal from './legal/AceptaLegal.jsx';
import { useLegalPendiente } from './legal/useLegalPendiente.js';

function ProtectedRoute() {
  const [isAuthenticated, setIsAuthenticated] = useState(null); // null = cargando, true = OK, false = No autenticado
  const [needsOnboarding, setNeedsOnboarding] = useState(null); // null = cargando, true = necesita, false = no necesita

  // ── LA BARRERA DE QUIEN SE QUEDÓ SIN ACCESO ────────────────────────────
  // Va aquí y no en cada pantalla porque por aquí pasan TODAS las rutas de
  // alumno: una barrera repartida por veinte rutas es una barrera con veinte
  // sitios donde olvidarla, y basta con que se escriba una ruta nueva sin
  // acordarse para abrir el agujero otra vez.
  //
  // Solo aparta a quien se le acabó la PRUEBA (`prueba_vencida`, que decide el
  // backend), y lo aparta de TODO, panel incluido: ahí no tiene ninguna
  // suscripción que gestionar. A quien sí tuvo plan —o nunca tuvo ninguno— NO
  // se le echa del panel: ese es el sitio donde lo activa o lo renueva, y un
  // muro que también tapa la salida no vende nada (decisión de Rob, 11-09-2026).
  //
  // Al resto de las rutas de alumno les llega igual, pero por el otro guardia:
  // `SubscriptionGuard` manda todo `overdue` a `/reactivar` (17-09-2026). Los
  // dos llevan al mismo sitio a propósito, para que no haya dos avisos para un
  // solo hecho como los había hasta hoy.
  const { pruebaVencida, loading: cargandoPlan } = useSubscriptionStatus();

  // ── EL AVISO DE PRIVACIDAD DEL EMPLEADO DE EMPRESA (T2.5) ──────────────
  // Aquí por la misma razón: todas las rutas de alumno pasan por este sitio.
  // Quien entra por el programa de su empresa lee qué ve su empresa ANTES de
  // entrar a nada. A quien no es empleado el backend le dice que no aplica.
  const aviso = useAvisoEmpresa();

  // ── LOS TÉRMINOS Y EL AVISO DE PRIVACIDAD (LEGAL.md, L4) ───────────────
  // A las cuentas que ya existían se les pide una vez, SIN bloquear: la
  // tarjeta se pinta al lado de la pantalla que pidieron, no en su lugar.
  const legal = useLegalPendiente();

  useEffect(() => {
    const checkUserStatus = async () => {
      const token = localStorage.getItem('token');

      if (!token) {
        setIsAuthenticated(false);
        // Añadimos esto para que la carga se complete
        setNeedsOnboarding(false); 
        return;
      }

      try {
        // --- INICIO DE LA CORRECCIÓN ---
        // Usamos la ruta relativa para que el proxy de Vite funcione
        const response = await fetch('/api/profile/me', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        // --- FIN DE LA CORRECCIÓN ---

        if (response.ok) {
          const userData = await response.json();
          setIsAuthenticated(true);
          
          // Con perfil y con profesión, adentro. Con perfil pero SIN profesión
          // —el que viene del diagnóstico de la puerta— queda una sola
          // pregunta, y la pantalla de onboarding la hace en un paso
          // (22-09-2026).
          //
          // ── PERO EL ONBOARDING ES DE ALUMNO, Y SÓLO DE ALUMNO ────────────
          //  Pregunta a qué se dedica y para qué quiere el inglés. A un
          //  profesor o a gerencia no les dice nada, y encima les cerraba la
          //  puerta: un profesor creado desde el CRM no tiene
          //  `learning_profile` —nunca hizo el diagnóstico de alumno— así que
          //  esta guardia le mandaba al onboarding, el onboarding le devolvía
          //  a la app, y vuelta a empezar. Dando vueltas sin llegar nunca.
          //
          //  Eso obligaba a dejarles fuera de las rutas protegidas, que es
          //  como el profesor acabó aterrizando en su agenda y no con Spacy.
          //  Rob, 27-09-2026: «al profesor lo va a recibir Spacy». Para que
          //  eso sea posible, la puerta tiene que dejarle pasar.
          const perfil = userData.learning_profile;
          const esAlumno = (userData.user_type || 'alumno') === 'alumno';
          setNeedsOnboarding(esAlumno && (!perfil || !perfil.profession));

        } else {
          localStorage.removeItem('token');
          setIsAuthenticated(false);
          setNeedsOnboarding(false);
        }
      } catch (error) {
        console.error("Error de red al verificar el estado del usuario:", error);
        setIsAuthenticated(false);
        setNeedsOnboarding(false);
      }
    };

    checkUserStatus();
  }, []);

  if (isAuthenticated === null || needsOnboarding === null || cargandoPlan || aviso.cargando) {
    return <div>Cargando...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (needsOnboarding) {
    return <Navigate to="/onboarding" />;
  }

  // `replace` a propósito: sin él, el botón de atrás del navegador devuelve al
  // alumno a la pantalla de la que se le acaba de apartar, y la barrera se
  // convierte en un parpadeo que se esquiva pulsando atrás.
  if (pruebaVencida) {
    return <Navigate to="/reactivar" replace />;
  }

  // En lugar de la pantalla pedida, no encima: así no carga nada de detrás
  // mientras lee. Al aceptar, la ruta que pidió sale sola.
  if (aviso.pendiente) {
    return <AvisoEmpresa datos={aviso.datos} onAceptado={aviso.aceptado} onRecarga={aviso.recarga} />;
  }

  return (
    <>
      <Outlet />
      {legal.pendiente ? <AceptaLegal documentos={legal.documentos} onAceptado={legal.listo} /> : null}
    </>
  );
}

export default ProtectedRoute;